import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import { ChakraProvider } from "@chakra-ui/react";
import { SkipNavLink } from "@chakra-ui/skip-nav";
import { theme } from "@/theme/index";

import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { replaceIdiomaticTextsWithSvgs } from "@/utils/fontAwesomeUtils";

import jQuery from "jquery";

import ms from "ms";
import { initDatadogRum } from "@/utils/datadogUtils";
import Root from "@/js/components/Root";

// normalize needs to come in first before ALL other styles
import "normalize.css";
import "@/scss/main.scss";
import { initI18n } from "@/i18n";

// Makes jQuery available in the global scope.
// I don't think this is required anymore as the only two places I can see jQuery being used are in
// authorisedApi.js and ClassroomToolMenu/index.jsx and both of these are now using the import statement.
declare global {
	interface Window {
		$: typeof jQuery;
		jQuery: typeof jQuery;
	}
}
window.$ = window.jQuery = jQuery;

initDatadogRum();
initI18n();

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			retry: 3,
			structuralSharing: false,

			// cacheTime controls how long to keep the data in the Tanstack Query cache. 5 minutes is also the default when it's not defined.
			// staleTime controls how long cached data is considered non-stale (0 means it's considered stale immediately after resolving it). 0 is also the default when it's not defined.
			// The effect of this is that when a component renders, it receives the cached data first and then triggers a fetch to get the new data.
			// This allows rendering of the old data first while the fresh data is fetched. This provides a better experience when the user is moving through the application quickly,
			// e.g. they go into a classroom and then immediately go back to the home screen, this allows the home screen to load quickly as it reuses the previous data immediately while making a fetch for
			// new data.
			cacheTime: ms("5m"),
			staleTime: 0,
		},
	},
});

replaceIdiomaticTextsWithSvgs();

const container = document.getElementById("root") as HTMLElement;
const root = createRoot(container);

root.render(
	<ChakraProvider theme={theme}>
		<QueryClientProvider client={queryClient}>
			<BrowserRouter>
				<SkipNavLink className="skip-link" tabIndex={0}>
					Skip navigation
				</SkipNavLink>
				<Root />
			</BrowserRouter>
			<ReactQueryDevtools initialIsOpen={false} />
		</QueryClientProvider>
	</ChakraProvider>
);
